<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import type { Product, StitchLength } from "@/types/product-types"
import { addStitchLength, editStitchLengthForProduct, deleteStitchLengthFromProduct, getProductById } from "@/api/products"
import { useProductSpecificationForm } from "@/composables/useProductSpecificationForm"
import { generateUniqueId } from "@/constants"
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import Button from 'primevue/button'
import { Icon } from '@iconify/vue'
import router from "@/router"


const { loading, showToast } = useProductSpecificationForm('view')
const editingRowId = ref<string | null>(null)
const stitchLengths = ref<(StitchLength & { isNew?: boolean })[]>([])
const originalRowData = ref<StitchLength | null>(null)
const productId = router.currentRoute.value.params.id as string
const isAddingNew = ref<boolean>(false)



const isRowEditable = (rowId?: string) => {
  return editingRowId.value === rowId
}

const handleEnableEdit = (row: StitchLength) => {
  originalRowData.value = JSON.parse(JSON.stringify(row))
  editingRowId.value = row.id!
}

const addNewStitchLengthRow = () => {
  const newId = generateUniqueId()
  const newRow: StitchLength & { isNew?: boolean } = {
    id: newId,
    reference: '',
    lengthValue: 0,
    lengthUnit: 0,
    numberOfNeedles: 0,
    numberOfMachineRevs: 0,
    isNew: true,
  }

  stitchLengths.value.push(newRow)
  editingRowId.value = newId
  isAddingNew.value = true
}

const handleSave = async (id?: string) => {
  if (!id) return
  const item = stitchLengths.value.find((item) => item.id === id)
  if (!item) return

  editingRowId.value = null
  isAddingNew.value = false

  const payload: StitchLength = {
    reference: item.reference,
    lengthValue: item.lengthValue,
    lengthUnit: item.lengthUnit,
    numberOfNeedles: item.numberOfNeedles,
    numberOfMachineRevs: item.numberOfMachineRevs,
  }

  loading.value = true
  try {
    await addStitchLength(productId, payload)
    showToast('success', 'Stitch length added successfully')
    const updatedProduct = await getProductById(productId)
    stitchLengths.value = updatedProduct.stitchLengths || []
  } catch (error) {
    showToast('error', 'Failed to add stitch length')
    stitchLengths.value = stitchLengths.value.filter(sl => sl.id !== id)
  } finally {
    loading.value = false
  }
}

const handleUpdate = async (id?: string) => {
  if (!id) return
  const item = stitchLengths.value.find((item) => item.id === id)
  if (!item) return

  editingRowId.value = null

  const payload: StitchLength = {
    reference: item.reference,
    lengthValue: item.lengthValue,
    lengthUnit: item.lengthUnit,
    numberOfNeedles: item.numberOfNeedles,
    numberOfMachineRevs: item.numberOfMachineRevs,
  }

  loading.value = true
  try {
    await editStitchLengthForProduct(productId, payload, item.id)
    showToast('success', 'Stitch length updated successfully')
    const updatedProduct = await getProductById(productId)
    stitchLengths.value = updatedProduct.stitchLengths || []
  } catch (error) {
    console.error('Failed to update stitch length', error)
    showToast('error', 'Failed to update stitch length')
    if (originalRowData.value) {
      const index = stitchLengths.value.findIndex(sl => sl.id === id)
      if (index !== -1) {
        stitchLengths.value[index] = { ...originalRowData.value }
      }
    }
  } finally {
    loading.value = false
    originalRowData.value = null
  }
}

const handleDelete = (stitchLengthId: string) => {
  deleteStitchLength(stitchLengthId)
}

const deleteStitchLength = async (stitchLengthId: string) => {
  loading.value = true
  try {
    await deleteStitchLengthFromProduct(productId, stitchLengthId)
    showToast('success', 'Stitch length deleted successfully')
    const updatedProduct = await getProductById(productId)
    stitchLengths.value = updatedProduct.stitchLengths || []
  } catch (error) {
    showToast('error', 'Failed to delete stitch length')
  } finally {
    loading.value = false
  }
}

const handleCancel = (row: StitchLength & { isNew?: boolean }) => {
  if (row.isNew) {
    stitchLengths.value = stitchLengths.value.filter(sl => sl.id !== row.id)
    isAddingNew.value = false
  } else {
    const index = stitchLengths.value.findIndex(sl => sl.id === row.id)
    if (index !== -1 && originalRowData.value) {
      stitchLengths.value[index] = { ...originalRowData.value }
    }
  }
  editingRowId.value = null
  originalRowData.value = null
}

onMounted(async () => {
  if (!productId) return
  loading.value = true
  try {
    const product: Product = await getProductById(productId)
    stitchLengths.value = product.stitchLengths?.map((item) => ({ ...item })) || []
  } catch (error) {
    console.error("Failed to fetch product data:", error)
    showToast('error', 'Failed to load stitch lengths')
  } finally {
    loading.value = false
  }
})

</script>

<template>
  <DataTable
    :value="stitchLengths"
    paginator :rows="10"
    table-style="min-width: 50rem" striped-rows
    paginator-template="PrevPageLink CurrentPageReport NextPageLink"
    :rows-per-page-options="[10, 25]"
    current-page-report-template="Showing {first} to {last} of {totalRecords} stitch lengths"
    :loading="loading"
    :pt="{ table: { class: [{ 'min-h-[30rem]': loading }] } }"
    style="width: 100%"
  >
    <Column field="reference" header="Reference" style="width: 20%">
      <template #body="slotProps">
        <InputText
          v-model="slotProps.data.reference"
          :class="isRowEditable(slotProps.data.id) ? '' : 'border-none bg-transparent'"
          :readonly="!isRowEditable(slotProps.data.id)"
          class="w-full"
        />
      </template>
    </Column>

    <Column field="lengthValue" header="Stitch Length" style="width: 20%">
      <template #body="slotProps">
        <InputNumber
          v-model="slotProps.data.lengthValue"
          :class="isRowEditable(slotProps.data.id) ? '' : 'border-none bg-transparent'"
          :disabled="!isRowEditable(slotProps.data.id)"
          class="w-full"
        />
      </template>
    </Column>

    <Column field="numberOfNeedles" header="Needles" style="width: 20%">
      <template #body="slotProps">
        <InputNumber
          v-model="slotProps.data.numberOfNeedles"
          :class="isRowEditable(slotProps.data.id) ? '' : 'border-none bg-transparent'"
          :disabled="!isRowEditable(slotProps.data.id)"
          class="w-full"
        />
      </template>
    </Column>

    <Column field="numberOfMachineRevs" header="Machine Revs" style="width: 20%">
      <template #body="slotProps">
        <InputNumber
          v-model="slotProps.data.numberOfMachineRevs"
          :class="isRowEditable(slotProps.data.id) ? '' : 'border-none bg-transparent'"
          :disabled="!isRowEditable(slotProps.data.id)"
          class="w-full"
        />
      </template>
    </Column>

    <Column header="Actions" style="width: 20%">
      <template #body="slotProps">
        <div v-if="!isRowEditable(slotProps.data.id)">
          <div class="flex gap-1">
            <Button
              v-ripple
              outlined
              size="small"
              class="flex-1 gap-x-2"
              @click="handleEnableEdit(slotProps.data)"
            >
              <span class="text-xs">Edit</span>
              <Icon icon="basil:edit-outline" class="text-lg"/>
            </Button>
            <Button
              v-ripple
              outlined
              severity="danger"
              size="small"
              class="flex-1 gap-x-2"
              @click="handleDelete(slotProps.data.id!)"
            >
              <span class="text-xs">Delete</span>
              <Icon icon="basil:trash-outline" class="text-lg"/>
            </Button>
          </div>
        </div>

        <div v-else class="flex gap-1">
          <Button
            v-if="slotProps.data.isNew"
            v-ripple
            outlined
            size="small"
            class="flex-1 gap-x-2"
            @click="handleSave(slotProps.data.id)"
            :loading="loading"
          >
            <span class="text-xs">Save</span>
            <Icon icon="basil:check-outline" class="text-xl"/>
          </Button>
          <Button
            v-else
            v-ripple
            outlined
            size="small"
            class="flex-1 gap-x-2"
            @click="handleUpdate(slotProps.data.id)"
            :loading="loading"
          >
            <span class="text-xs">Update</span>
            <Icon icon="basil:refresh-outline" class="text-xl"/>
          </Button>
          <Button
            v-ripple
            outlined
            severity="secondary"
            size="small"
            class="flex-1 gap-x-2"
            @click="handleCancel(slotProps.data)"
            :disabled="loading"
          >
            <span class="text-xs">Cancel</span>
            <Icon icon="basil:close-outline" class="text-xl"/>
          </Button>
        </div>
      </template>
    </Column>
  </DataTable>

  <div class="px-4 py-2 mt-2">
    <Button
      v-ripple outlined class="gap-x-2 w-full sm:w-auto"
      @click="addNewStitchLengthRow()"
      :loading="loading"
      :disabled="isAddingNew"
    >
      <span>Add New Stitch Length</span>
      <Icon icon="basil:plus-solid" class="text-2xl"/>
    </Button>
  </div>
</template>

<style scoped>
.p-inputtext.border-none, .p-inputnumber.border-none {
  cursor: default;
  pointer-events: none;
}
.p-inputtext, .p-inputnumber {
  width: 100%;
}
</style>